import React, {useEffect, useState} from 'react';
import {
    Stack,
    Typography,
    IconButton,
    Button,
    TextField,
    Autocomplete,
    AutocompleteRenderInputParams,
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import {Dayjs} from 'dayjs';
import {FileDataDialog, FilesCategoryType, FileTag, ReportType} from '@pages/FilesPage';
import {FileTagChip} from '@features/files/FileTagChip';
import {FileType} from '@features/files/types';
import {DocumentDatePicker} from './DocumentDatePicker';
import {useTranslation} from 'react-i18next';

interface FileItemProps {
    index: number;
    fileData: FileDataDialog;
    onFilenameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDocumentDateChange: (newValue: Dayjs | null) => void;
    onDeleteFile: () => void;
    onDeleteTag: (tagId: string) => void;
    onOpenTagSidebar: () => void;
    isEditing?: boolean;
    onChangeType: (newValue: ReportType | null) => void;
    category?: FilesCategoryType | null;
    filesType?: FileType;
    showAddTagButton?: boolean;
}

const fileTypeMap = new Map([
    [FileType.PLAN, ReportType.PLAN],
    [FileType.FACT, ReportType.FACT],
    [null, null],
]);

export const UploadFileItem = ({
    fileData,
    onFilenameChange,
    onDocumentDateChange,
    onDeleteFile,
    onDeleteTag,
    onOpenTagSidebar,
    isEditing,
    onChangeType,
    category,
    filesType,
    showAddTagButton = true,
}: FileItemProps) => {
    const initialValue = () => {
        if (isEditing) {
            return fileTypeMap.get(fileData.type) ?? null;
        }
        return filesType === FileType.FACT ? ReportType.FACT : ReportType.PLAN;
    };

    const [value, setValue] = useState<ReportType | null>(initialValue());

    const {t} = useTranslation(['supervising', 'common']);

    useEffect(() => {
        onChangeType(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <Stack spacing={1}>
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
            >
                <Stack
                    direction='row'
                    gap='16px'
                >
                    <InsertDriveFileOutlinedIcon color='primary' />
                    <Typography
                        variant='subtitle1'
                        flexWrap='wrap'
                        sx={{wordBreak: 'break-all'}}
                    >
                        {fileData.file.name}
                    </Typography>
                </Stack>
                {!isEditing && (
                    <IconButton onClick={onDeleteFile}>
                        <DeleteOutlinedIcon />
                    </IconButton>
                )}
            </Stack>

            {category?.hasPlanFact ? (
                <Stack
                    direction='column'
                    spacing={1}
                    alignItems='center'
                >
                    <Stack
                        direction='row'
                        gap={1}
                        width='100%'
                    >
                        <Autocomplete
                            size='small'
                            sx={{flex: '2'}}
                            options={[ReportType.PLAN, ReportType.FACT]}
                            value={value}
                            onChange={(_, newValue: ReportType | null) => {
                                setValue(newValue);
                            }}
                            renderInput={(params: AutocompleteRenderInputParams) => (
                                <TextField
                                    {...params}
                                    label={t('type')}
                                />
                            )}
                        />
                        <DocumentDatePicker
                            value={fileData.documentDate}
                            onChange={onDocumentDateChange}
                            flex
                        />
                    </Stack>
                    <TextField
                        label={t('title')}
                        value={fileData.filename}
                        onChange={onFilenameChange}
                        fullWidth
                        size='small'
                        disabled
                    />
                </Stack>
            ) : (
                <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                >
                    <DocumentDatePicker
                        value={fileData.documentDate}
                        onChange={onDocumentDateChange}
                    />
                    <TextField
                        label={t('title')}
                        value={fileData.filename}
                        onChange={onFilenameChange}
                        fullWidth
                        size='small'
                        disabled
                    />
                </Stack>
            )}

            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='start'
            >
                <Stack
                    direction='row'
                    gap={1}
                    flexWrap='wrap'
                >
                    {fileData.tags?.map((tag: FileTag) => (
                        <FileTagChip
                            key={tag.id}
                            tag={tag}
                            onDelete={() => onDeleteTag(tag.id)}
                        />
                    ))}
                </Stack>
                {showAddTagButton && (
                    <Button
                        sx={{minWidth: '170px'}}
                        startIcon={<AddIcon />}
                        onClick={onOpenTagSidebar}
                    >
                        {t('uploadFileItem.addTag')}
                    </Button>
                )}
            </Stack>
        </Stack>
    );
};
